import api from "@/api"

export const rosterMarkerShapes = [
  { value: "circle", text: "Kreis", icon: "mdi-circle-outline" },
  { value: "square", text: "Quadrat", icon: "mdi-square-outline" },
  { value: "triangle", text: "Dreieck", icon: "mdi-triangle-outline" },
  { value: "star", text: "Stern", icon: "mdi-star-outline" },
]

function minutes(value) {
  if (typeof value !== "string" || !/^(?:[01]?\d|2[0-3]):[0-5]\d$|^24:00$/.test(value)) return null
  const [hours, mins] = value.split(":").map(Number)
  return hours * 60 + mins
}

// "check" means the request or shift lacks exact times (e.g. half-day holidays).
// It must never be presented as proof that a wish can be approved.
export function provisionalConflict(request, shifts) {
  let ranges = [[0, 1440]]
  let uncertain = request.isHalfDay === true || (Number(request.percentage) < 100 && !request.timeRange)
  if (request.period === "morning") ranges = [[0, 720]]
  if (request.period === "afternoon") ranges = [[720, 1440]]
  if (request.timeRange) {
    ranges = [...request.timeRange.matchAll(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/g)]
      .map((match) => [minutes(match[1]), minutes(match[2])])
      .filter(([start, end]) => start !== null && end !== null && start < end)
    if (!ranges.length) { ranges = [[0, 1440]]; uncertain = true }
  }
  let needsCheck = false
  for (const shift of shifts) {
    const offset = (Date.parse(shift.date) - Date.parse(request.date)) / 86400000
    if (offset !== 0 && offset !== -1) continue
    const blocks = Array.isArray(shift.times) && shift.times.length ? shift.times : shift.shiftTemplate?.times || []
    if (!blocks.length || shift.isVirtualExternal) { if (offset === 0) needsCheck = true; continue }
    for (const block of blocks) {
      if (String(block.type || "").toLowerCase() === "pause") continue
      const start = minutes(block.start), end = minutes(block.end)
      if (start === null || end === null || start === end) { if (offset === 0) needsCheck = true; continue }
      const from = start + offset * 1440
      const to = end + offset * 1440 + (end < start ? 1440 : 0)
      if (ranges.some(([a, b]) => from < b && to > a)) {
        if (!uncertain) return "overlap"
        needsCheck = true
      }
    }
  }
  return needsCheck ? "check" : null
}

export default {
  props: { planningStudioIds: { type: Array, default: () => [] } },
  data() {
    return {
      rosterMarkers: [], rosterProvisionalAbsences: [], rosterToolsLoading: false, rosterToolsError: "", rosterLoadId: 0,
      showProvisional: localStorage.getItem("rosterShowProvisional") === "true",
      dayToolsDialog: false, dayToolsTarget: null, markerEditing: false, markerSaving: false,
      rosterHasHover: false, rosterHasTouch: false, rosterPointerType: "", activeRosterMenu: null,
      markerDraft: { shape: "circle", color: "#fbbf24", comment: "" },
      markerShapes: rosterMarkerShapes,
      markerColors: [
        { value: "#fbbf24", text: "Gelb" }, { value: "#f87171", text: "Rot" },
        { value: "#60a5fa", text: "Blau" }, { value: "#34d399", text: "Grün" },
        { value: "#c084fc", text: "Violett" }, { value: "#f472b6", text: "Pink" },
      ],
    }
  },
  computed: {
    // Keep cell geometry stable while an iPad switches between a finger and a trackpad.
    rosterTouchLayout() { return this.rosterHasTouch || !this.rosterHasHover },
    rosterTouchMode() { return !this.rosterHasHover || this.rosterPointerType === "touch" || this.rosterPointerType === "pen" },
    rosterTooltipsEnabled() {
      return !this.rosterTouchMode && !this.activeRosterMenu && !this.dayToolsDialog && !this.moveSource &&
        !this.assignDialog && !this.addShiftDialog && !this.swapDialog && !this.releaseConflictDialog
    },
    selectedDayStatus() {
      return this.dayToolsTarget?.employee?.dailyStatus?.[this.dayToolsTarget.date] || null
    },
    selectedDayShifts() {
      const status = this.selectedDayStatus
      return status && ["shift", "externalShift"].includes(status.type) ? status.allShifts || [status.data] : []
    },
    rosterStudioIds() { return [...new Set([this.studioId, ...(this.planningStudioIds || [])].filter(Boolean))] },
    rosterScope() { return `${this.startDate}|${this.rosterStudioIds.join(",")}` },
    rosterMarkerMap() { return Object.fromEntries(this.rosterMarkers.map((marker) => [this.markerKey(marker.studio, marker.date, marker.employee), marker])) },
    rosterProvisionalMap() {
      if (!this.showProvisional) return {}
      const visibleEmployeeIds = new Set(this.employees.map((employee) => String(employee._id)))
      const shiftsByEmployee = {}
      for (const shift of [...this.planData, ...this.externalShifts, ...(this.previousContext?.shifts || [])]) {
        if (!shift.assignedEmployee || shift.status === "unassigned" || shift.deleted === true || shift.isDeleted === true) continue
        const id = String(shift.assignedEmployee._id || shift.assignedEmployee)
        if (!shiftsByEmployee[id]) shiftsByEmployee[id] = []
        shiftsByEmployee[id].push(shift)
      }
      const map = {}
      for (const request of this.rosterProvisionalAbsences) {
        if (!visibleEmployeeIds.has(String(request.employee))) continue
        const key = `${request.employee}|${request.date}`
        if (!map[key]) map[key] = { requests: [], needsCheck: false }
        const conflict = provisionalConflict(request, shiftsByEmployee[request.employee] || [])
        map[key].requests.push({ ...request, conflict })
        if (conflict) map[key].needsCheck = true
      }
      return map
    },
    provisionalDayCounts() {
      const counts = {}
      for (const cell of Object.values(this.rosterProvisionalMap)) {
        const date = cell.requests[0].date
        counts[date] = (counts[date] || 0) + 1
      }
      return counts
    },
    provisionalSummary() {
      const cells = Object.values(this.rosterProvisionalMap)
      return { days: cells.length, checks: cells.filter((cell) => cell.needsCheck).length }
    },
    rosterAnnotatedEmployees() {
      const ids = new Set(this.rosterMarkers.filter((marker) => marker.employee).map((marker) => String(marker.employee)))
      for (const cell of Object.values(this.rosterProvisionalMap)) ids.add(cell.requests[0].employee)
      return ids
    },
    selectedMarker() {
      const target = this.dayToolsTarget
      return target ? this.rosterMarkerMap[this.markerKey(target.studio, target.date, target.employeeId)] : null
    },
    selectedProvisionalRequests() {
      const target = this.dayToolsTarget
      if (!target || !this.showProvisional) return []
      return Object.values(this.rosterProvisionalMap).flatMap((cell) => cell.requests)
        .filter((request) => request.date === target.date && (!target.employeeId || request.employee === target.employeeId))
    },
  },
  watch: {
    rosterScope: { immediate: true, handler() {
      this.activeRosterMenu = null
      this.dayToolsDialog = false
      this.dayToolsTarget = null
      this.markerSaving = false
      this.rosterMarkers = []
      this.rosterProvisionalAbsences = []
      this.loadRosterTools()
    } },
    showProvisional(value) {
      localStorage.setItem("rosterShowProvisional", String(value))
      this.loadRosterTools()
    },
  },
  mounted() {
    if (typeof window === "undefined" || !window.matchMedia) return
    this._rosterHoverQuery = window.matchMedia("(any-hover: hover) and (any-pointer: fine)")
    this.rosterHasTouch = window.matchMedia("(any-pointer: coarse)").matches || (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0)
    this._rosterHoverChanged = (event) => { this.rosterHasHover = event.matches }
    this.rosterHasHover = this._rosterHoverQuery.matches
    if (this._rosterHoverQuery.addEventListener) this._rosterHoverQuery.addEventListener("change", this._rosterHoverChanged)
    else if (this._rosterHoverQuery.addListener) this._rosterHoverQuery.addListener(this._rosterHoverChanged)
  },
  beforeDestroy() {
    this.rosterLoadId += 1
    if (this._rosterHoverQuery?.removeEventListener) this._rosterHoverQuery.removeEventListener("change", this._rosterHoverChanged)
    else if (this._rosterHoverQuery?.removeListener) this._rosterHoverQuery.removeListener(this._rosterHoverChanged)
  },
  methods: {
    recordRosterPointer(event) {
      const pointerType = event.pointerType || (event.type === "touchstart" ? "touch" : "")
      if (!pointerType || pointerType === this.rosterPointerType) return
      this.rosterPointerType = pointerType
      // Only real pointer events can restore mouse hover after a finger tap.
      // Safari's compatibility mouse events must not open a second surface.
      if (pointerType !== "mouse") this.activeRosterMenu = null
    },
    rosterHoverListeners(listeners) { return this.rosterTooltipsEnabled ? listeners : {} },
    rosterShiftMenuListeners(listeners) { return !this.rosterTouchMode && !this.moveSource ? listeners : {} },
    setRosterMenu(key, open) {
      if (open && (this.dayToolsDialog || (key.startsWith("shift-") && (this.rosterTouchMode || this.moveSource)))) return
      if (open) this.activeRosterMenu = key
      else if (this.activeRosterMenu === key) this.activeRosterMenu = null
    },
    runDayShiftAction(action, shift) {
      const employee = this.dayToolsTarget?.employee
      if (!employee || this.markerSaving) return
      this.dayToolsDialog = false
      if (action === "move") this.startMove(shift)
      else if (action === "delete") this.deleteShift(shift)
      else if (action === "assistant") this.openAssistantForSchoolConflict(shift)
      else if (action === "release") this.openReleaseSchoolConflict(shift, employee)
      else if (action === "settings") this.openEmployeeSettingsForConflict(employee)
      else if (action === "keep") this.keepSchoolConflict()
    },
    markerKey(studio, date, employee) { return `${studio}|${date}|${employee || "day"}` },
    markerFor(employee, date) {
      return this.rosterMarkerMap[this.markerKey(employee?.loadedFromStudioId || this.studioId, date, employee?._id)]
    },
    markerIcon(shape) { return this.markerShapes.find((item) => item.value === shape)?.icon || "mdi-circle-outline" },
    provisionalFor(employee, date) { return this.rosterProvisionalMap[`${employee._id}|${date}`] },
    provisionalEmployeeName(id) {
      return [...this.employees, ...this.availableEmployees].find((employee) => String(employee._id) === id)?.fullName || "Mitarbeiter"
    },
    provisionalDetail(request) {
      if (request.timeRange) return request.timeRange
      if (request.isHalfDay) return "Halber Tag · Tageshälfte offen"
      if (request.period === "morning") return "Vormittag (bis 12:00)"
      if (request.period === "afternoon") return "Nachmittag (ab 12:00)"
      if (request.percentage != null && Number(request.percentage) < 100) return `${request.percentage}% · Zeitfenster offen`
      return "Ganzer Tag"
    },
    async loadRosterTools() {
      const requestId = ++this.rosterLoadId
      this.rosterToolsError = ""
      this.rosterProvisionalAbsences = []
      if (!this.studioId || !this.startDate) { this.rosterToolsLoading = false; return }
      this.rosterToolsLoading = true
      try {
        const endDate = this.$moment(this.startDate).endOf("month").format("YYYY-MM-DD")
        const results = await Promise.all(this.rosterStudioIds.map((studio) => api.shiftManagement.fetchRosterTools(studio, this.startDate, endDate, this.showProvisional)))
        if (requestId !== this.rosterLoadId) return
        this.rosterMarkers = results.flatMap((result) => result.markers || [])
        this.rosterProvisionalAbsences = [...new Map(results.flatMap((result) => result.provisionalAbsences || []).map((request) => [request.key, request])).values()]
      } catch (error) {
        if (requestId !== this.rosterLoadId) return
        this.rosterToolsError = "Markierungen und provisorische Wünsche konnten nicht vollständig geladen werden."
      } finally {
        if (requestId === this.rosterLoadId) this.rosterToolsLoading = false
      }
    },
    openDayTools(employee, date) {
      if (this.moveSource) return
      this.activeRosterMenu = null
      this.dayToolsTarget = { studio: employee?.loadedFromStudioId || this.studioId, date, employeeId: employee ? String(employee._id) : null, employee }
      this.markerEditing = false
      this.dayToolsDialog = true
    },
    editDayMarker() {
      const marker = this.selectedMarker
      this.markerDraft = marker ? { shape: marker.shape, color: marker.color, comment: marker.comment } : { shape: "circle", color: "#fbbf24", comment: "" }
      this.markerEditing = true
    },
    async saveDayMarker(remove = false) {
      if (this.markerSaving || this.rosterToolsLoading || this.rosterToolsError || !this.dayToolsTarget || this.markerDraft.comment.length > 240) return
      const target = this.dayToolsTarget, scope = this.rosterScope, generation = this.rosterLoadId
      this.markerSaving = true
      try {
        const marker = remove
          ? await api.shiftManagement.deleteRosterMarker(target.studio, target.date, target.employeeId)
          : await api.shiftManagement.saveRosterMarker(target.studio, target.date, target.employeeId, { ...this.markerDraft })
        if (scope !== this.rosterScope || generation !== this.rosterLoadId) return
        const key = this.markerKey(target.studio, target.date, target.employeeId)
        this.rosterMarkers = this.rosterMarkers.filter((item) => this.markerKey(item.studio, item.date, item.employee) !== key)
        if (!remove) this.rosterMarkers.push(marker)
        this.markerEditing = false
        this.$toast.success(remove ? "Markierung entfernt." : "Markierung gespeichert.")
      } catch (error) {
        if (scope === this.rosterScope && generation === this.rosterLoadId) this.$toast.error(error.response?.data?.message || "Markierung konnte nicht gespeichert werden.")
      } finally {
        if (scope === this.rosterScope && generation === this.rosterLoadId) this.markerSaving = false
      }
    },
    addShiftFromDayTools() {
      const target = this.dayToolsTarget
      if (!target?.employee) return
      this.dayToolsDialog = false
      this.openAddShiftDialog(target.employee, target.date)
    },
  },
}
